import { useState, useEffect, useMemo, useCallback, useContext, useRef } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import { BiSearch } from "react-icons/bi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";
import CourseService from "../../../services/CourseService";

const CourseUsers = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { state } = useLocation();
  const authContext = useContext(AuthContext);
  // const [previewed, setPreviewed] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [courseUsers, setCourseUsers] = useState([]);
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");
  const [selectAllLeft, setSelectAllLeft] = useState(false);
  const [selectAllRight, setSelectAllRight] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [removedUsers, setRemovedUsers] = useState([]);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [initialAssignments, setInitialAssignments] = useState([]);
  const submitControllerRef = useRef(null); 


  const courseId = params.id || state?.id;

  const getFormattedCourseName = (raw) => {
    const formatted = raw?.replace(/-/g, " ");
    return formatted?.replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const courseName = getFormattedCourseName(params?.courseName);
  const courseAccess = authContext.getPermissions("courses");
  const showAdd = courseAccess.filter((f) => f.access.includes(2)).length > 0;
  const showEdit = courseAccess.filter((f) => f.access.includes(3)).length > 0;

const loadCourseUsers = useCallback(async (signal) => {
    setIsInitialLoading(true);
    try {
      const data = await CourseService.GetUsers(
        courseId,
        "",
        authContext,
        signal
      );

      if (signal?.aborted) return;

      if (data.isSuccess) {
        const processedMapped =
          data.data?.mappedUsers?.map((user) => ({
            ...user,
            selected: false,
            difficulty: user.difficulty || 1,
          })) || [];

        const processedAll =
          data.data?.allUsers?.map((user) => ({
            ...user,
            selected: false,
          })) || [];

        setCourseUsers(processedMapped);
        setInitialAssignments(processedMapped);

        if (processedMapped.length > 0) {
          setUsers(
            processedAll.filter(
              (f) => !processedMapped.some((d) => d.userId === f.userId)
            )
          );
        } else {
          setUsers(processedAll);
        }
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      AlertService.error("Failed to load users");
    } finally {
      if (!signal?.aborted) setIsInitialLoading(false); 
    }
  }, [courseId, authContext]);

  const handlePreviewClick = (e) => {
    e.preventDefault();
    if (!hasChanges) {
      AlertService.warning("No changes to preview");
      return;
    }
    setShowChangesModal(true);
  };

 useEffect(() => {
    const controller = new AbortController();
    if (courseId) {
      loadCourseUsers(controller.signal);
    } else {
      navigate("/");
    }
    return () => controller.abort();
  }, [courseId, loadCourseUsers, navigate]);

  useEffect(() => {
    const currentUserIds = courseUsers.map((u) => u.userId);
    const newlyAssigned = courseUsers.filter(
      (user) =>
        !initialAssignments.some((initUser) => initUser.userId === user.userId)
    );
    const removed = initialAssignments.filter(
      (initUser) => !currentUserIds.includes(initUser.userId)
    );

    setAssignedUsers(newlyAssigned);
    setRemovedUsers(removed);
  }, [courseUsers, initialAssignments]);

  // const updateDifficulty = (userId, difficulty) => {
  //   setCourseUsers((prev) =>
  //     prev.map((user) =>
  //       user.userId === userId ? { ...user, difficulty } : user
  //     )
  //   );
  // };

  const hasChanges = useMemo(() => {
    return assignedUsers.length > 0 || removedUsers.length > 0;
  }, [assignedUsers, removedUsers]);

  const filteredLeftUsers = useMemo(() => {
    return users.filter((user) =>
      user.userName.toLowerCase().includes(searchLeft.toLowerCase())
    );
  }, [users, searchLeft]);

  const filteredRightUsers = useMemo(() => {
    return courseUsers.filter((user) =>
      user.userName.toLowerCase().includes(searchRight.toLowerCase())
    );
  }, [courseUsers, searchRight]);

  useEffect(() => {
    if (filteredLeftUsers.length > 0) {
      setSelectAllLeft(filteredLeftUsers.every((user) => user.selected));
    } else {
      setSelectAllLeft(false);
    }
  }, [filteredLeftUsers]);

  useEffect(() => {
    if (filteredRightUsers.length > 0) {
      setSelectAllRight(filteredRightUsers.every((user) => user.selected));
    } else {
      setSelectAllRight(false);
    }
  }, [filteredRightUsers]);

  // User selection handlers
  const toggleUserSelection = (userId, isRightPanel = false) => {
    const setter = isRightPanel ? setCourseUsers : setUsers;
    // const usersList = isRightPanel ? courseUsers : users;

    setter((prev) =>
      prev.map((user) =>
        user.userId === userId ? { ...user, selected: !user.selected } : user
      )
    );
  };

  const toggleSelectAll = (isRightPanel = false) => {
    const setter = isRightPanel ? setCourseUsers : setUsers;
    const filtered = isRightPanel ? filteredRightUsers : filteredLeftUsers;
    const allSelected = isRightPanel ? selectAllRight : selectAllLeft;

    const userIdsToUpdate = filtered.map((u) => u.userId);

    setter((prev) =>
      prev.map((user) =>
        userIdsToUpdate.includes(user.userId)
          ? { ...user, selected: !allSelected }
          : user
      )
    );
  };
  // User movement functions
  const moveUsers = (direction) => {
    if (direction === "right") {
      const selectedUsers = users.filter((user) => user.selected);
      const usersWithDifficulty = selectedUsers.map((user) => ({
        ...user,
        selected: false,
        difficulty: user.difficulty || 1,
      }));

      setCourseUsers((prev) => [...prev, ...usersWithDifficulty]);
      setUsers((prev) => prev.filter((user) => !user.selected));
    } else {
      const selectedUsers = courseUsers.filter((user) => user.selected);
      setUsers((prev) => [
        ...prev,
        ...selectedUsers.map(({ difficulty, ...rest }) => rest),
      ]);
      setCourseUsers((prev) => prev.filter((user) => !user.selected));
    }
  };

  // Actual save operation after confirmation
const confirmSubmit = async () => {
    if (submitControllerRef.current) {
      submitControllerRef.current.abort(); 
    }
    const controller = new AbortController();
    submitControllerRef.current = controller;

    setIsSaving(true);
    setShowChangesModal(false);

    try {
      const payload = {
        courseId: courseId,
        users: courseUsers.map((user) => ({
          userId: user.userId,
          difficulty: user.difficulty,
        })),
      };

      const data = await CourseService.SaveUsers(
        payload,
        authContext,
        controller.signal 
      );

      if (controller.signal.aborted) return;

      if (data.isSuccess) {
        AlertService.success("User assignments updated successfully");
        loadCourseUsers();
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      AlertService.error("Failed to save user assignments");
    } finally {
      if (!controller.signal.aborted) setIsSaving(false);
    }
  };

  // Component unmount
useEffect(() => {
    return () => {
      submitControllerRef.current?.abort();
    };
  }, []);


  // Modal footer template
  const modalFooter = (
    <div>
      <button
        className="btn btn-outline-secondary px-5 py-2 me-4"
        onClick={() => setShowChangesModal(false)}
      >
        Cancel
      </button>
      <button
        className="btn btn-primary px-5 py-2"
        onClick={async () => {
          await confirmSubmit();
          setShowChangesModal(false);
          // setPreviewed(true);
        }}
      >
        Submit
      </button>
    </div>
  );

  // Render skeleton rows
  const renderSkeletonRows = (count) => {
    return Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="d-flex align-items-center pb-2 pt-2 ps-3 border-bottom"
      >
        <div className="me-2">
          <Skeleton shape="circle" size="20px" />
        </div>
        <Skeleton width="90%" height="1.5rem" />
      </div>
    ));
  };
  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="container-fluid custom-container  mt-4 mb-3">
      <div className="row g-1">
        {/* Left Panel - User Master Repository */}
        <div
          className="col-12 col-md-7 border rounded card p-0"
          style={{ minWidth: "350px" }}
        >
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom bg-section rounded-3">
            <div className="d-flex align-items-center">
              {isInitialLoading ? (
                <Skeleton width="24px" height="24px" className="me-2" />
              ) : (
                <Checkbox
                  checked={selectAllLeft}
                  onChange={() => toggleSelectAll(false)}
                  disabled={filteredLeftUsers.length === 0}
                  className="me-2"
                />
              )}
              <span className="fw-bold">
                {isInitialLoading ? (
                  <Skeleton width="120px" />
                ) : (
                  "User’s Masters"
                )}
              </span>
            </div>
            <div className="position-relative">
              {isInitialLoading ? (
                <Skeleton width="100%" height="38px" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchLeft}
                    onChange={(e) => setSearchLeft(e.target.value)}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>
          <div
            className="pt-1 scroll-no-buttons"
            style={{ height: "660px", overflowY: "auto" }}
          >
            {isInitialLoading || isSaving ? (
              renderSkeletonRows(16)
            ) : filteredLeftUsers.length > 0 ? (
              <>
              
                {filteredLeftUsers.map((user, i) => (
                  <div
                    key={user.userId}
                    className="d-flex align-items-center pb-2 pt-2 ps-3 border-bottom flex-wrap"
                  >
                    <Checkbox
                      checked={user.selected}
                      onChange={() => toggleUserSelection(user.userId, false)}
                      className="flex-shrink-0"
                    />
                    <span
                      className="ms-3 text-truncate"
                      style={{
                        minWidth: "60px",
                        maxWidth: "80px",
                        flexGrow: 0,
                      }}
                    >
                      {(i + 1).toString().padStart(3, '0')}
                    </span>
                    <span
                      className="me-4 text-truncate"
                      style={{
                        minWidth: "120px",
                        maxWidth: "140px",
                        flexGrow: 1,
                      }}
                    >
                      {user.userCode}
                    </span>
                    <span
                      className="me-4 text-truncate"
                      style={{
                        minWidth: "150px",
                        maxWidth: "200px",
                        flexGrow: 2,
                      }}
                    >
                      {user.displayName}
                    </span>
                    <span
                      className="me-4 text-truncate"
                      style={{
                        minWidth: "110px",
                        maxWidth: "130px",
                        flexGrow: 1,
                      }}
                    >
                      {user.mobileNo}
                    </span>
                    <span
                      className="text-truncate"
                      style={{
                        minWidth: "180px",
                        maxWidth: "220px",
                        flexGrow: 3,
                      }}
                    >
                      {user.userName}
                    </span>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-center p-5 text-muted">
                No users available
              </div>
            )}
          </div>
        </div>

        {/* Middle buttons */}
        <div className="col-12 col-md-2 col-lg-1 d-flex flex-md-column justify-content-center align-items-center gap-3">
          {showAdd && (
            <Button
              icon="pi pi-arrow-right"
              className="btn border-0 fw-bold"
              style={{ backgroundColor: "#9CF", minWidth: "60px" }}
              onClick={() => moveUsers("right")}
              disabled={
                isInitialLoading ||
                isSaving ||
                !users.some((user) => user.selected)
              }
            />
          )}
          {showEdit && (
            <Button
              icon="pi pi-arrow-left"
              className="btn border-0 fw-bold"
              style={{ backgroundColor: "#9CF", minWidth: "60px" }}
              onClick={() => moveUsers("left")}
              disabled={
                isInitialLoading ||
                isSaving ||
                !courseUsers.some((user) => user.selected)
              }
            />
          )}
        </div>

        {/* Right Panel - Assigned Users */}
        <div
          className="col-12 col-md-4 border rounded p-0 card"
          style={{ minWidth: "250px" }}
        >
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom rounded-3 bg-section">
            <div className="d-flex align-items-center">
              {isInitialLoading ? (
                <Skeleton width="24px" height="24px" className="me-2" />
              ) : (
                <Checkbox
                  checked={selectAllRight}
                  onChange={() => toggleSelectAll(true)}
                  disabled={filteredRightUsers.length === 0}
                  className="me-2"
                />
              )}
              <span className="fw-bold">
                {isInitialLoading ? (
                  <Skeleton width="130px" />
                ) : (
                  "Assigned Users"
                )}
              </span>
            </div>
            <div className="position-relative">
              {isInitialLoading ? (
                <Skeleton width="100%" height="38px" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchRight}
                    onChange={(e) => setSearchRight(e.target.value)}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>
          <div
            className="pt-1 scroll-no-buttons"
            style={{ height: "660px", overflowY: "auto" }}
          >
            {isInitialLoading || isSaving ? (
              renderSkeletonRows(16)
            ) : filteredRightUsers.length > 0 ? (
              <>
            
                {filteredRightUsers.map((user, i) => (
                  <div
                    key={user.userId}
                    className="d-flex align-items-center pb-2 pt-2 ps-3 border-bottom flex-wrap"
                  >
                    <Checkbox
                      checked={user.selected}
                      onChange={() => toggleUserSelection(user.userId, true)}
                      className=""
                    />
                    <span
                      className="ms-3 me-3"
                      style={{
                        minWidth: "60px",
                        maxWidth: "80px",
                        flexGrow: 0,
                      }}
                    >
                      {(i + 1).toString().padStart(3, '0')}
                    </span>
                    <span
                      className="me-3"
                      style={{
                        minWidth: "120px",
                        maxWidth: "140px",
                        flexGrow: 1,
                      }}
                    >
                      {user.userCode}
                    </span>
                    <span
                      className="me-3"
                      style={{
                        minWidth: "150px",
                        maxWidth: "200px",
                        flexGrow: 2,
                      }}
                    >
                      {user.userName}
                    </span>

                    {/* <div className="ms-auto d-flex me-2 border rounded">
                                        {[1, 2, 3].map(level => (
                                            <button
                                                key={level}
                                                type="button"
                                                className={`btn ${user.difficulty === level
                                                    ? 'btn-primary'
                                                    : 'bg-white'
                                                    }`}
                                                style={{
                                                    borderRight: level < 3 ? '1px solid #dee2e6' : 'none',
                                                    borderRadius: level === 1 ? '4px 0 0 4px' :
                                                        level === 3 ? '0 4px 4px 0' : '0',
                                                    width: '40px'
                                                }}
                                                onClick={() => updateDifficulty(user.userId, level)}
                                            >
                                                {level === 1 ? 'E' : level === 2 ? 'M' : 'H'}
                                            </button>
                                        ))}
                                    </div> */}
                  </div>
                ))}
              </>
            ) : (
              <div className="text-center p-5 text-muted">
                No users assigned
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12 d-flex justify-content-end gap-4">
          <Button
            label="Cancel"
            className="btn btn-outline px-5"
            onClick={() => handleCancel()}
            disabled={isInitialLoading || isSaving}
          />
          <Button
            className="btn btn-primary px-5 py-2"
            onClick={handlePreviewClick}
            disabled={isInitialLoading || isSaving || !hasChanges}
          >
            Preview
          </Button>

        </div>
      </div>

      {/* Changes Preview Modal */}
      <Dialog
        header={<div className="w-100 text-center">Preview</div>}
        visible={showChangesModal}
        style={{ width: "40vw", height: "70vh" }}
        contentStyle={{ maxHeight: "60vh", overflowY: "auto" }}
        footer={modalFooter}
        onHide={() => setShowChangesModal(false)}
      >
        <div className="mb-4">
          <h5 className="mb-3">
            <strong>Course Name</strong>: {courseName}
          </h5>
          {assignedUsers.length > 0 && (
            <div>
              <h5>
                {assignedUsers.length === 1
                  ? "Assigned User"
                  : "Assigned Users"}
              </h5>
              <ul className="list-group">
                {assignedUsers.map((user) => (
                  <li key={user.userId} className="list-group-item text-primary">
                    {user.userName}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {removedUsers.length > 0 && (
            <div>
              <h5>
                {removedUsers.length === 1
                  ? "Unassigned User"
                  : "Unassigned Users"}
              </h5>
              <ul className="list-group">
                {removedUsers.map((user) => (
                  <li key={user.userId} className="list-group-item text-primary">
                    {user.userName}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default CourseUsers;
